import { z } from 'zod';
import { PLATFORMS, isMain, isOfferedOnPlatform, hasReelDeliverable } from '@discovr/types';
import type { CreateCampaignRequest } from '@discovr/types';
import { USAGE_RIGHTS_MIN_DAYS, USAGE_RIGHTS_MAX_DAYS, MAX_PRODUCTS } from './constants';
import { buildProductsPayload } from './helpers';

// Mirrors the BE's domain.CampaignCreateBudgetMax (create_constraints.go) —
// without this, an absurdly long digit string can push a JS number toward
// Number.MAX_SAFE_INTEGER (or beyond, into imprecise/Infinity territory)
// with no field-level error, only a late BE rejection.
export const CAMPAIGN_BUDGET_MAX = 1_000_000_000_000_000;

// Single source for the payoutModel/totalBudget cross-field rule — used by
// the object-level refine below AND by NewCampaign.tsx's step-3 "ready" gate,
// which used to duplicate this logic and once drifted (it omitted
// revenue_share entirely, defaulting it into the fixed_fee branch). Mirrors
// the BE rule in campaigns/domain.ValidateBudgetForPayout.
export function isBudgetValidForPayout(
  payoutModel: 'fixed_fee' | 'revenue_share' | 'gifting_only' | undefined,
  totalBudget: number | undefined,
): boolean {
  if (payoutModel !== 'fixed_fee') return true;
  return totalBudget != null && totalBudget >= 1;
}

// One product row in the Multi-Product Campaigns catalog (brand-entered). name is
// required; link/note optional. Mapped to the BE domain.Product shape
// (name / product_url / notes) at submit.
export type ProductRow = { name: string; link?: string; note?: string };

// Mirrors backend guardrails in internal/campaigns/dto.go + campaign.go.
// V2 alpha (2026-05-02): targeting multi-selects (gender / language / region /
// creatorGenders) are now fully optional — empty array = "no filter", which is
// the industry standard for influencer-marketing brief tooling. The legacy
// 'Any' sentinel was confusing UX and tripped server-side enum validation when
// it leaked through. Drafts in localStorage may still contain 'Any'; the
// stripAny helper below + the FE submit-time filter scrub it defensively.
const ALLOWED_GENDERS = ['male', 'female', 'non-binary'] as const;
const ALLOWED_LANGUAGES = [
  'arabic',
  'bengali',
  'english',
  'gujarati',
  'hindi',
  'kannada',
  'malayalam',
  'marathi',
  'spanish',
  'tamil',
  'telugu',
] as const;
const ALLOWED_TIERS = [
  '1000-10000',
  '10000-50000',
  '50000-100000',
  '100000-500000',
  '500000-1000000',
  '1000000-10000000',
] as const;

const stripAny = (arr: string[]): string[] =>
  arr.map((v) => v.trim()).filter((v) => v && v.toLowerCase() !== 'any');

const eachIn = (arr: string[], allowed: readonly string[]): boolean =>
  arr.every((v) => allowed.includes(v.toLowerCase()));


export const campaignSchema = z
  .object({
    // Step 1 — campaign basics
    platform: z.enum(PLATFORMS, { error: 'Select a platform' }),
    name: z
      .string()
      .trim()
      .min(1, 'Campaign name is required')
      .max(200, 'Campaign name must be 200 characters or fewer'),
    primaryGoal: z
      .string()
      .min(1, 'Select a primary goal')
      .refine(
        (v) =>
          ['product_launch', 'brand_awareness', 'lead_generation', 'sales', 'engagement'].includes(
            v,
          ),
        { message: 'Select a primary goal' },
      ),
    primaryGoalCustom: z.string().optional(),
    // Campaign description — maps to the BE's top-level `description`, which
    // is required there (min 1 char). Optional in the wizard: the submit
    // handler falls back to a placeholder ("Pending brief details.") when
    // left blank, so the BE's non-empty constraint is still satisfied.
    brief: z
      .string()
      .trim()
      .max(8000, 'Description must be 8000 characters or fewer')
      .optional(),

    // Step 2 — creator & audience targeting
    totalCreators: z.coerce
      .number()
      .int('Creator count must be a whole number')
      .min(1, 'At least 1 creator required')
      .max(2000, 'Maximum 2000 creators allowed'),
    creatorCategories: z
      .array(z.string().trim().min(1).max(100, 'Category must be 100 characters or fewer'))
      .min(1, 'Select at least one category'),
    creatorGenders: z
      .array(z.string())
      .optional()
      .default([])
      .refine((arr) => stripAny(arr).length <= 3, {
        message: 'Select up to 3 creator genders',
      })
      .refine((arr) => eachIn(stripAny(arr), ALLOWED_GENDERS), {
        message: 'Creator gender must be male, female, or non-binary',
      }),
    creatorGenderNotes: z
      .string()
      .max(500, 'Notes must be 500 characters or fewer')
      .optional(),
    tierPresets: z
      .array(z.string())
      .min(1, 'Select at least one tier')
      .max(6, 'Select up to 6 tiers')
      .refine((arr) => new Set(arr).size === arr.length, { message: 'Tiers must be unique' })
      .refine((arr) => arr.every((t) => (ALLOWED_TIERS as readonly string[]).includes(t)), {
        message: 'Invalid tier selected',
      }),
    ageMin: z.coerce
      .number()
      .int('Age must be a whole number')
      .min(13, 'Minimum age is 13')
      .max(90, 'Age must be 90 or below'),
    ageMax: z.coerce
      .number()
      .int('Age must be a whole number')
      .min(13, 'Age must be at least 13')
      .max(90, 'Maximum age is 90'),
    gender: z
      .array(z.string())
      .optional()
      .default([])
      .refine((arr) => stripAny(arr).length <= 3, {
        message: 'Select up to 3 genders',
      })
      .refine((arr) => eachIn(stripAny(arr), ALLOWED_GENDERS), {
        message: 'Gender must be male, female, or non-binary',
      }),
    language: z
      .array(z.string())
      .optional()
      .default([])
      .refine((arr) => eachIn(stripAny(arr), ALLOWED_LANGUAGES), {
        message: 'Unsupported language selected',
      }),
    // V2 (2026-05-02): region is brand-side context, not a hard
    // targeting filter today — accept anything in REGIONS (see
    // NewCampaign.tsx). The ALLOWED_REGIONS allowlist used to be
    // ['india']; dropped so brands can express interest in USA / UAE /
    // UK / Global without a Zod refusal. BE side already accepts these.
    region: z.array(z.string()).optional().default([]),

    // Step 3 — deliverables & budget
    // Array of deliverable type IDs (e.g. ["youtube_integrated", "youtube_shorts"]).
    // At least one main-category type is required — enforced by the object-level
    // refine below (platform is needed, so it can't be a field rule).
    deliverables: z.array(z.string()).optional().default([]),
    // Free-form description of what creators should deliver.
    deliverables_text: z
      .string()
      .trim()
      .max(20000, 'Deliverables text must be 20,000 characters or fewer')
      .optional()
      .default(''),
    // Usage rights — optional; required only when opted in (object is nullable).
    usageRights: z
      .object({
        days: z.coerce
          .number({ error: 'Duration is required' })
          .int('Enter a whole number of days')
          .min(USAGE_RIGHTS_MIN_DAYS, `Minimum ${USAGE_RIGHTS_MIN_DAYS} days`)
          .max(USAGE_RIGHTS_MAX_DAYS, 'Maximum 3 years (1095 days)'),
      })
      .nullable()
      .optional(),
    // Official Audio Compliance — the verified IG audio URL all creators must
    // use (Instagram only, optional). Empty = not set. Sent as part of the
    // same create body (buildCampaignPayload's official_audio field), not a
    // separate post-create call.
    officialAudioUrl: z.string().optional().default(''),
    // The song/artist the FE resolved when the brand verified the audio —
    // carried through the form so it can be sent in the create payload,
    // storing the campaign's song name reliably (the backend re-resolve hits
    // IG's consent wall). Display/persist only; not a required input.
    officialAudioTrack: z
      .object({
        audio_id: z.string(),
        song_name: z.string().optional(),
        artist_name: z.string().optional(),
        // Cover art resolved at Verify — carried through so it's persisted with
        // the audio and the campaign detail view can render it like the
        // reporting "Sound used" cell.
        cover_url: z.string().optional(),
      })
      .nullable()
      .optional(),
    // undefined ("never typed a budget") is distinct from 0 ("explicitly
    // typed zero") — the campaign details page shows "₹0" for the latter and
    // hides the budget line for the former. Mirrors the BE's
    // domain.Campaign.TotalBudget *float64. Only reachable for
    // gifting_only/revenue_share: fixed_fee never actually submits undefined,
    // since the object-level refine below rejects it.
    totalBudget: z.coerce
      .number()
      .int('Budget must be a whole number (no cents)')
      // Non-negative at the field level — 0 is valid for gifting_only. The
      // payout-specific rule (non-gifting must be >= 1, and must be present)
      // is enforced in the object-level refine below, where payoutModel is
      // in scope.
      .nonnegative('Budget cannot be negative')
      .max(CAMPAIGN_BUDGET_MAX, 'Budget is too large')
      .optional(),
    startDate: z.string().min(1, 'Start date is required'),
    payoutModel: z.enum(['fixed_fee', 'revenue_share', 'gifting_only'], {
      error: 'Select a payout model',
    }),

    // Step 4 — campaign brief
    videoTitle: z.string().max(200, 'Title must be 200 characters or fewer').optional(),
    videoDescription: z
      .string()
      .max(4000, 'Description must be 4000 characters or fewer')
      .optional(),
    primaryFocus: z
      .string()
      .trim()
      .min(1, 'Primary Focus of campaign is required!')
      // Bumped 2026-04-28 from 120 to 2000 — kept in sync with BE
      // CampaignCreateBriefPrimaryFocusMaxLen.
      .max(2000, 'Primary Focus must be 2000 characters or fewer'),
    secondaryFocus: z
      .string()
      // Bumped 2026-04-28 from 500 to 5000 — kept in sync with BE
      // CampaignCreateBriefSecondaryFocusMaxLen.
      .max(5000, 'Secondary focus must be 5000 characters or fewer')
      .optional(),
    cta: z.string().max(200, 'CTA must be 200 characters or fewer').optional(),
    requiresDisclosure: z.enum(['yes', 'no']).optional(),
    // Array of items end-to-end — matches the BE CampaignBrief.Dos/Donts
    // ([]string, max 20 items, each 1–500 chars). No markdown round-trip.
    dos: z
      .array(z.string().min(1, 'Each "Do" must be at least 1 character').max(500, 'Each "Do" must be 500 characters or fewer'))
      .max(20, 'Up to 20 "Do" items')
      .optional(),
    donts: z
      .array(z.string().min(1, 'Each "Don\'t" must be at least 1 character').max(500, 'Each "Don\'t" must be 500 characters or fewer'))
      .max(20, 'Up to 20 "Don\'t" items')
      .optional(),
    sampleVideoUrl: z
      .union([
        z.literal(''),
        z.url('Enter a valid URL').max(2048, 'URL must be 2048 characters or fewer'),
      ])
      .optional(),
    // Multi-Product Campaigns — optional products catalog. Fully-blank rows (no
    // name/link/note typed at all — a freshly-added row nobody has touched)
    // are dropped before validation so that row never blocks the wizard. This
    // check is deliberately RAW (not trimmed): a row where the user typed only
    // whitespace is left in the array on purpose, so it falls through to the
    // name field's `.trim().min(1, 'Product name is required')` below and
    // surfaces a real error instead of the row silently vanishing with no
    // feedback. Any row with content must have a name. Mirrors the BE
    // domain.Product tags (name 1–200, url ≤2048, notes ≤2000). 0 or 1
    // product keeps the feature dormant downstream (activation gate is >= 2).
    products: z.preprocess(
      (val) =>
        Array.isArray(val)
          ? val.filter(
              (p) =>
                p != null &&
                ((p.name ?? '') !== '' || (p.link ?? '') !== '' || (p.note ?? '') !== ''),
            )
          : [],
      z
        .array(
          z.object({
            name: z
              .string()
              .trim()
              .min(1, 'Product name is required')
              .max(200, 'Product name must be 200 characters or fewer'),
            link: z
              .union([
                z.literal(''),
                z.url('Enter a valid product URL').max(2048, 'URL must be 2048 characters or fewer'),
              ])
              .optional(),
            note: z.string().max(2000, 'Note must be 2000 characters or fewer').optional(),
          }),
        )
        // MAX_PRODUCTS (./constants.ts — also re-exported from
        // ./helpers.ts for existing consumers there) is what the
        // wizard's "Add product" button actually enforces, and also mirrors
        // the BE's validate:"omitempty,max=10,dive" tag on
        // CreateCampaignRequest.Products (dto.go) — imported rather than a
        // second hardcoded 10 so the two can't silently drift apart.
        .max(MAX_PRODUCTS, `Up to ${MAX_PRODUCTS} products`),
    ),
  })
  .refine((d) => d.ageMax >= d.ageMin, {
    message: 'Maximum age must be greater than or equal to minimum age',
    path: ['ageMax'],
  })
  // total_budget may be undefined or 0 for gifting_only and revenue_share;
  // fixed_fee requires it present AND >= 1. See isBudgetValidForPayout above
  // — also used by NewCampaign.tsx's step-3 "ready" gate, so the two rules
  // can't drift the way they did before (the gate used to omit
  // revenue_share entirely). Mirrors the BE rule in
  // campaigns/domain.ValidateBudgetForPayout.
  .refine((d) => isBudgetValidForPayout(d.payoutModel, d.totalBudget), {
    message: 'Budget must be at least ₹1 for Fixed Fee campaigns',
    path: ['totalBudget'],
  })
  // Both platforms: at least one main content-type card must be selected —
  // add-ons alone (Link in Bio, Auto DM, Usage Rights) do not count. The free
  // text stays optional everywhere. Mirrors the BE rule in
  // campaigns/domain.NormalizeDeliverables.
  .refine((d) => (d.deliverables ?? []).some((t) => isMain(t, d.platform)), {
    message: 'Select at least one content type',
    path: ['deliverables'],
  });

export type CampaignFormData = z.infer<typeof campaignSchema>;

// Fields validated per step — passed to trigger() on Next click
export const stepFields: Record<number, (keyof CampaignFormData)[]> = {
  1: ['platform', 'name', 'primaryGoal', 'brief'],
  2: [
    'totalCreators',
    'creatorCategories',
    'creatorGenders',
    'creatorGenderNotes',
    'tierPresets',
    'ageMin',
    'ageMax',
    'gender',
    'language',
    'region',
  ],
  3: ['deliverables', 'deliverables_text', 'usageRights', 'officialAudioUrl', 'totalBudget', 'startDate', 'payoutModel'],
  4: [
    'primaryFocus',
    'videoTitle',
    'videoDescription',
    'secondaryFocus',
    'cta',
    'requiresDisclosure',
    'dos',
    'donts',
    'sampleVideoUrl',
    'products',
  ],
  5: [],
};

export const defaultValues: CampaignFormData = {
  platform: 'youtube',
  name: '',
  primaryGoal: '',
  primaryGoalCustom: '',
  brief: '',
  totalCreators: 2,
  creatorCategories: [],
  creatorGenders: [],
  creatorGenderNotes: '',
  tierPresets: [],
  ageMin: 20,
  ageMax: 55,
  gender: [],
  language: [],
  region: [],
  deliverables: [],
  deliverables_text: '',
  usageRights: null,
  officialAudioUrl: '',
  officialAudioTrack: null,
  // undefined, not 0 — the common case (a brand who never touches this
  // field, e.g. because they picked gifting_only) must be treated as "no
  // budget was even specified," not as an explicit ₹0.
  totalBudget: undefined,
  startDate: '',
  payoutModel: 'fixed_fee',
  videoTitle: '',
  videoDescription: '',
  primaryFocus: '',
  secondaryFocus: '',
  cta: '',
  requiresDisclosure: 'yes',
  dos: [],
  donts: [],
  sampleVideoUrl: '',
  products: [],
};

// Which step a field belongs to — used to jump the wizard to the first step
// with a validation error (see NewCampaign.tsx's onSubmit error handler).
export const FIELD_TO_STEP: Partial<Record<keyof CampaignFormData, number>> = {
  platform: 1, name: 1, primaryGoal: 1, primaryGoalCustom: 1, brief: 1,
  totalCreators: 2, creatorCategories: 2, creatorGenders: 2, creatorGenderNotes: 2, tierPresets: 2,
  ageMin: 2, ageMax: 2, gender: 2, language: 2, region: 2,
  deliverables: 3, deliverables_text: 3, totalBudget: 3, startDate: 3, payoutModel: 3, usageRights: 3,
  videoTitle: 4, videoDescription: 4, primaryFocus: 4, secondaryFocus: 4, cta: 4, requiresDisclosure: 4,
  dos: 4, donts: 4, sampleVideoUrl: 4, products: 4,
};

// Per-step mandatory-field checks — mirrors campaignSchema's required fields,
// used to gate the final Launch button. Steps 5/6 have no inputs of their
// own, so they're always satisfied.
export function computeStepComplete(data: CampaignFormData): Record<number, boolean> {
  const creatorCategories = data.creatorCategories ?? [];
  const tierPresets = data.tierPresets ?? [];
  const deliverables = data.deliverables ?? [];
  return {
    1: Boolean(data.name.trim()) && Boolean(data.platform) && Boolean(data.primaryGoal),
    2:
      Number(data.totalCreators) >= 1 &&
      creatorCategories.length > 0 &&
      tierPresets.length > 0 &&
      data.ageMin != null &&
      data.ageMax != null &&
      Number(data.ageMin) >= 13 &&
      Number(data.ageMin) <= 90 &&
      Number(data.ageMax) >= 13 &&
      Number(data.ageMax) <= 90 &&
      Number(data.ageMax) >= Number(data.ageMin),
    3:
      Boolean(data.payoutModel) &&
      Boolean(data.startDate) &&
      isBudgetValidForPayout(data.payoutModel, data.totalBudget) &&
      deliverables.some((t) => isMain(t, data.platform)),
    4: Boolean((data.primaryFocus ?? '').trim()),
    5: true,
    6: true,
  };
}

// Builds the campaign-create request body (BE field names) from the wizard's
// form data. Official Audio Compliance ships in this same body (official_audio,
// below) when it qualifies — see the Instagram+Reel gate a few lines down.
export function buildCampaignPayload(data: CampaignFormData): CreateCampaignRequest {
  return {
    name: data.name.trim(),
    // Step 1's "Campaign description" field is optional in the wizard;
    // the BE's top-level `description` is required (min 1 char), so an
    // empty field falls back to a placeholder rather than blocking submit.
    description: (data.brief ?? '').trim() || 'Pending brief details.',
    platform: data.platform,
    primary_goal: data.primaryGoal,
    // null (not 0) when the brand never typed a budget, so the BE can
    // distinguish "no budget specified" from an explicit 0.
    total_budget: data.totalBudget ?? null,
    payout_model: data.payoutModel,
    // Multi-Product Campaigns: optional catalog. Empty keeps the campaign
    // single-product. Name-less rows are dropped (see buildProductsPayload).
    products: buildProductsPayload(data.products ?? []),
    deliverables_text: (data.deliverables_text ?? '').trim(),
    // usage_rights is Instagram-only, so nothing merges elsewhere.
    deliverables:
      isOfferedOnPlatform('usage_rights', data.platform) &&
      data.usageRights != null &&
      !(data.deliverables ?? []).includes('usage_rights')
        ? [...(data.deliverables ?? []), 'usage_rights']
        : (data.deliverables ?? []),
    usage_rights: isOfferedOnPlatform('usage_rights', data.platform)
      ? (data.usageRights ?? null)
      : null,
    creator_preference: {
      creator_count: Number(data.totalCreators),
      categories: data.creatorCategories,
      tiers: data.tierPresets,
      // empty arrays mean "no filter" — see notes above. Defensive
      // lower-case + Any-strip handles stale drafts.
      creator_genders: (data.creatorGenders ?? [])
        .filter((g) => g && g.toLowerCase() !== 'any')
        .map((g) => g.toLowerCase()),
      gender_notes: (data.creatorGenderNotes ?? '').trim(),
    },
    targeting: {
      demographics: {
        age_min: Number(data.ageMin),
        age_max: Number(data.ageMax),
        genders: (data.gender ?? [])
          .filter((g) => g && g.toLowerCase() !== 'any')
          .map((g) => g.toLowerCase()),
        languages: (data.language ?? [])
          .filter((l) => l && l.toLowerCase() !== 'any')
          .map((l) => l.toLowerCase()),
        regions: (data.region ?? [])
          .filter((r) => r && r.toLowerCase() !== 'any')
          .map((r) => r.toLowerCase()),
      },
    },
    brief: {
      primary_focus: (data.primaryFocus ?? '').trim(),
      video_title: (data.videoTitle ?? '').trim(),
      video_description: (data.videoDescription ?? '').trim(),
      secondary_focus: (data.secondaryFocus ?? '').trim(),
      cta: (data.cta ?? '').trim(),
      requires_disclosure: data.requiresDisclosure !== 'no',
      dos: Array.isArray(data.dos) ? data.dos : [],
      donts: Array.isArray(data.donts) ? data.donts : [],
      sample_video_url: (data.sampleVideoUrl ?? '').trim(),
    },
    // Official Audio Compliance — sent as part of the same create body now
    // (the BE used to reject audio at create time, requiring a separate
    // best-effort brandApi.setOfficialAudio call after the campaign existed).
    // Same Instagram+Reel gate as CampaignAudioSection's own visibility and
    // NewCampaign.tsx's clear-on-deselect effect — omitted entirely rather
    // than sent empty when the campaign doesn't qualify or nothing was verified.
    ...(data.platform === 'instagram' &&
    hasReelDeliverable(data.deliverables ?? []) &&
    (data.officialAudioUrl ?? '').trim()
      ? {
          official_audio: {
            audio_ids: data.officialAudioTrack?.audio_id ? [data.officialAudioTrack.audio_id] : [],
            audio_urls: [(data.officialAudioUrl ?? '').trim()],
            tracks: data.officialAudioTrack?.audio_id ? [data.officialAudioTrack] : [],
          },
        }
      : {}),
  };
}

